/**
 * Platform / install-state detection helpers.
 * Used by the app-install banner to decide what (if anything) to show.
 */

/**
 * Detect the mobile OS from the user agent.
 * @returns {'ios'|'android'|null} null when not a supported mobile platform (e.g. desktop)
 */
export function getMobilePlatform() {
    if (typeof navigator === 'undefined') return null;

    const ua = navigator.userAgent || navigator.vendor || '';

    // iPadOS 13+ reports as "MacIntel" but exposes touch points, so check that too.
    const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) return 'ios';
    if (/android/i.test(ua)) return 'android';

    return null;
}

/**
 * Whether the app is currently running as an installed (standalone) app,
 * either as an installed PWA or inside iOS' standalone web view.
 * @returns {boolean}
 */
export function isStandalone() {
    if (typeof window === 'undefined') return false;

    const displayModeStandalone =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches;

    // iOS Safari exposes this non-standard flag when launched from the home screen.
    const iosStandalone = window.navigator.standalone === true;

    return !!(displayModeStandalone || iosStandalone);
}

/**
 * Whether the PWA is loaded inside a native wrapper app / in-app web view
 * (rather than a real mobile browser). We don't want to invite people to
 * "download the app" when they are already inside it.
 *
 * Detection:
 *  - Explicit signal from the wrapper (most reliable): a `foubertapp` marker
 *    in the user agent, a `window.FoubertApp === true` flag the wrapper can
 *    inject, or a `?inapp=1` query param.
 *  - Heuristics: Android WebView ("; wv)"), and iOS WKWebView (an iOS device
 *    whose UA has no "Safari/" token - Safari always includes it, WKWebView
 *    does not).
 * @returns {boolean}
 */
export function isInAppWebView() {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || '').toLowerCase();

    // Explicit signals the native wrapper can set. The marker + window flag are
    // per-tenant (each customer's wrapper injects its own), configured via env.
    const uaMarker = process.env.REACT_APP_APP_UA_MARKER || 'foubertapp|foubert-app';
    const windowFlag = process.env.REACT_APP_APP_WINDOW_FLAG || 'FoubertApp';
    if (new RegExp(uaMarker).test(ua)) return true;
    if (typeof window !== 'undefined') {
        if (window[windowFlag] === true) return true;
        try {
            if (new URLSearchParams(window.location.search).get('inapp') === '1') return true;
        } catch { /* ignore */ }
    }

    // Android System WebView.
    if (/wv\)/.test(ua) || (/version\/[\d.]+/.test(ua) && /\bchrome\/[.0-9]* mobile\b/.test(ua) && /; wv/.test(ua))) {
        return true;
    }

    // iOS WKWebView: iOS device, WebKit, but no Safari token (real Safari has it).
    const isIOS =
        /iphone|ipad|ipod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS && /applewebkit/.test(ua) && !/safari/.test(ua) && !/crios|fxios|edgios/.test(ua)) {
        return true;
    }

    return false;
}
